import React, { useEffect } from "react";

import { useRealmApp } from "../../../RealmApp";
import DatatablePage from "../../../layout/datatable-pages/companies";
import { fetchData, getCompaniesFieldsArray } from "../../../utils/helpers";
import { props } from "../../datatable/props";
import useDatatable from "../../hooks/useDatatable";

import "../datatable-pages.css";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * DiscontinuedProducts Component
 * 
 * Displays a paginated datatable of discontinued/dead products with failure analysis.
 * Users can search through 28+ product and company attributes including product details,
 * failure reasons, financial metrics, and business context. Shows statistics on dead products
 * and reasons for discontinuation. Provides insights into why products failed and market lessons.
 * Requires user authentication to access.
 * 
 * @returns {JSX.Element} Datatable page with discontinued products data
 */
const DiscontinuedProducts = () => {
  // Array of field keys for products datatable
  const fieldKeys = [
    "tradeName",
    "stageOfDevelopment",
    "description",
    "reasonsForFailure",
    "categories",
    "industry",
    "location",
    "ownership",
    "businessModelType",
    "defensibilityMoat",
    "productReview",
    "lastFinancingRound",
    "totalInvestment",
    "postMoneyValuation",
    "lastFinancingDate",
    "numOfFundingRounds",
    "numOfInvestors",
    "investors",
    "competitors",
    "similarEntities",
    "management",
    "numOfEmployees",
    "phoneNumber",
    "operationalDuration",
    "shutdownDate",
    "foundedDate",
    "pitchdeck_show",
  ];

  // Convert field keys to formatted field objects
  const fields = getCompaniesFieldsArray(fieldKeys);

  // Get datatable state and props
  const { datatableProps } = useDatatable();

  // Get Supabase utilities and user context
  const { currentUser, supabase, getUserTier } = useSupabaseApp();

  // Verify user authentication
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // Configuration for discontinued products datatable
  const params = {
    supabase,
    ...datatableProps,
    getData: "getDiscontinuedProducts",
    collection: "companies",
    title: "Discontinued products",
    datatipText:
      "Discover problem-solving creativity from our collection of inactive products.",
    statsArray: [
      {
        actualName: "deadProducts",
        displayName: "Dead products",
        total: "417",
      },
      {
        actualName: "reasonsForDiscontinuation",
        displayName: "Reasons for discontinuation",
        total: "46",
      },
    ],
    placeholderText: "Search from discontinued products",
    fields: fields,
    getCompanyDetailsForExpand: "getCompanyDetailsForExpand",
  };

  // Fetch products data on component mount
  useEffect(() => {
    fetchData(params);
  }, []);

  // Render datatable page with configured params
  return <DatatablePage props={props(params)} />;
};

export default DiscontinuedProducts;
